import { websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { useSessionMetricsFilters } from '@va/dashboard/shared/filters';
import { useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { LatestVisitorsTable } from '@va/dashboard/shared/visitors';
import { useTranslate } from '@va/localization';
import { AppliedFilters, FiltersContextProvider } from '@va/shared/feature-filters';
import { DataViewOption } from '@va/types/report-block';
import { UserRole } from '@va/types/website';
import { ReportBlock } from '@va/ui/components/report-block';
import { ReportBlockNodeIds } from '@va/ui/components/report-block/nodes';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DownloadHeaderNode, DownloadMenuComponent } from './DownloadRBNode';
import { useLatestVisitorsInitialFilters } from './useLatestVisitorsInitialFilters';

export const LatestVisitorsTableReport = memo(({ id }: { id: string }) => {
  const { allFilters } = useSessionMetricsFilters();
  const buildReportBlockNode = useReportBlockNodeBuilder();
  const initialFilters = useLatestVisitorsInitialFilters();
  const translate = useTranslate();
  const isCustomDashboardViewer = useSelector(websiteRoleSelectors.hasUserRole)(UserRole.DASHBOARD);

  const nodes = useMemo(
    () => ({
      filterButton: buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON),
      columnVisibility: buildReportBlockNode(ReportBlockNodeIds.COLUMN_VISIBILITY),
      appliedFilters: <AppliedFilters />,
      ...(!isCustomDashboardViewer && {
        downloadButton: buildReportBlockNode(ReportBlockNodeIds.DOWNLOAD_BUTTON, {
          label: translate('all.exportType.scheduled'),
          headerComponent: <DownloadHeaderNode />,
          menuComponent: () => <DownloadMenuComponent />,
        }),
      }),
    }),
    [buildReportBlockNode, translate, isCustomDashboardViewer],
  );

  const visualizationComponents = useMemo(() => {
    return {
      [DataViewOption.table]: () => <LatestVisitorsTable defaultSorting={[{ id: 'sessionStartUnix', desc: true }]} />,
    };
  }, []);

  return (
    <FiltersContextProvider reportBlockId={id} allFilterOptions={allFilters} initialAppliedFilters={initialFilters}>
      <ReportBlock
        id={id}
        title={translate('card.latestVisitors.title')}
        titleTooltip={translate('card.latestVisitors.info.tooltip')}
        nodes={nodes}
        visualization={{ selectedView: DataViewOption.table }}
        visualizationComponents={visualizationComponents}
      />
    </FiltersContextProvider>
  );
});
